import { useEffect, useMemo, useRef, useState, type FormEvent, type InputHTMLAttributes, type ReactNode, type TextareaHTMLAttributes } from "react";
import { lockBodyScroll, unlockBodyScroll } from "../app/bodyScrollLock";
import { healthClasses, priorityClasses, statusClasses } from "../app/data";
import { customerDetailViewCopy } from "../app/i18n";
import { getIntlLocale, type AppLanguage } from "../app/language";
import { AnimatedModal, DeleteAccountButton, TicketLastUpdateText, UserAvatarBadge } from "../app/ui";
import { appendCustomerActionHistory, createRecordId, formatCount, formatDateForInput, formatDateLabel, getCustomerSeatCount, getInitials, resolveWorkspaceMember, syncCustomerFields } from "../app/utils";
import type { Customer, CustomerActionHistoryEntry, CustomerContact, CustomerNote, CustomerProduct, Expense, ExpenseVendor, ProductPlan, Ticket, WorkspaceMember } from "../app/types";

type CustomerDetailViewProps = {
  customer: Customer;
  productPlans: ProductPlan[];
  tickets: Ticket[];
  expenses?: Expense[];
  expenseVendors?: ExpenseVendor[];
  currency?: string;
  teamMembers: WorkspaceMember[];
  timezone: string;
  language: AppLanguage;
  currentUser: string;
  canEditCustomers?: boolean;
  canDeleteCustomers?: boolean;
  onBack: () => void;
  onOpenTicket: (ticketId: string) => void;
  onUpdateCustomer: (customer: Customer) => void;
  onDeleteCustomer: (customerId: string) => void;
};

type ContactDraft = {
  role: string;
  name: string;
  email: string;
  phone: string;
};

type ProductDraft = {
  productPlanId: string;
  seats: number;
  renewalDate: string;
};

type NoteDraft = {
  body: string;
};

type CustomerProfileDraft = {
  company: string;
  region: string;
};

const buttonClasses = "rounded-full px-4 py-2 text-[11px] font-semibold uppercase tracking-[0.18em] transition-colors";
const secondaryButtonClasses = `${buttonClasses} bg-[#f3f4f6] text-[#475467] hover:bg-[#e4e7ec]`;
const primaryButtonClasses = `${buttonClasses} brand-primary-solid`;
const mainActionButtonClasses = "brand-primary-button rounded-xl px-5 py-3 text-sm font-bold transition-all";
const subtleActionButtonClasses = "rounded-full bg-white px-3 py-1.5 text-[10px] font-semibold uppercase tracking-[0.18em] text-[#475467] shadow-[0_8px_20px_rgba(15,23,42,0.05)] transition-colors hover:bg-[#f8fafc]";
const modalCancelButtonClasses = "rounded-xl px-4 py-2.5 text-sm font-semibold text-[#475467] transition-colors hover:bg-[#f3f4f6]";
const modalPrimaryButtonClasses = "brand-primary-button rounded-xl px-5 py-2.5 text-sm font-bold transition-all";

const emptyContactDraft: ContactDraft = { role: "Primary", name: "", email: "", phone: "" };
const emptyNoteDraft: NoteDraft = { body: "" };
const customerHealthOptions = ["Healthy", "Watch", "Escalated", "Disabled"] as Customer["health"][];
type CustomerDetailCopy = typeof customerDetailViewCopy.en;

function getCustomerProfileDraft(customer: Customer): CustomerProfileDraft {
  return {
    company: customer.company,
    region: customer.region,
  };
}

function getEmptyProductDraft(productPlans: ProductPlan[]): ProductDraft {
  return {
    productPlanId: productPlans[0]?.id ?? "",
    seats: 1,
    renewalDate: "",
  };
}

function getDaysUntil(dateLabel: string) {
  const now = new Date();
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const isoMatch = /^(\d{4})-(\d{2})-(\d{2})$/.exec(dateLabel.trim());
  const renewalDate = isoMatch
    ? new Date(Number(isoMatch[1]), Number(isoMatch[2]) - 1, Number(isoMatch[3]))
    : new Date(dateLabel);
  const timeDifference = renewalDate.getTime() - today.getTime();

  return Math.ceil(timeDifference / (1000 * 60 * 60 * 24));
}

function getRenewalProgress(dateLabel: string) {
  const daysUntilRenewal = Math.max(0, getDaysUntil(dateLabel));
  const progress = ((365 - Math.min(daysUntilRenewal, 365)) / 365) * 100;

  return Math.min(100, Math.max(10, Math.round(progress)));
}

function getRenewalClasses(daysUntilRenewal: number) {
  if (daysUntilRenewal <= 45) {
    return {
      badge: "bg-[#fef3f2] text-[#b42318]",
      bar: "bg-[linear-gradient(90deg,#b42318,#f97066)]",
    };
  }

  if (daysUntilRenewal <= 90) {
    return {
      badge: "bg-[#fffaeb] text-[#b54708]",
      bar: "bg-[linear-gradient(90deg,#b54708,#f38744)]",
    };
  }

  return {
    badge: "bg-[#ecfdf3] text-[#027a48]",
    bar: "bg-[linear-gradient(90deg,#027a48,#6cdbaa)]",
  };
}

function formatNoteTimestamp(value: string, language: AppLanguage) {
  return new Intl.DateTimeFormat(getIntlLocale(language), {
    month: "short",
    day: "numeric",
    year: "numeric",
    hour: "numeric",
    minute: "2-digit",
  }).format(new Date(value));
}

const historyEntityClasses: Record<CustomerActionHistoryEntry["entity"], string> = {
  Product: "bg-[#ecfdf3] text-[#027a48]",
  Contact: "bg-[#eff8ff] text-[#175cd3]",
  "Account status": "bg-[#f2f4f7] text-[#475467]",
};

function sortContacts(contacts: CustomerContact[]) {
  const roleOrder: Record<string, number> = { Primary: 0, Secondary: 1 };

  return [...contacts].sort((left, right) => {
    const leftRank = roleOrder[left.role] ?? 99;
    const rightRank = roleOrder[right.role] ?? 99;

    if (leftRank !== rightRank) {
      return leftRank - rightRank;
    }

    return left.name.localeCompare(right.name);
  });
}

function ProductRenewalRow({ product, actions, copy }: { product: CustomerProduct; actions?: ReactNode; copy: CustomerDetailCopy }) {
  if (!product.renewalDate) {
    return (
      <div className="rounded-[22px] border border-[rgba(0,0,0,0.04)] bg-[#fcfcfd] p-5">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div>
            <p className="text-sm font-semibold text-[#101828]">{product.name}</p>
            <p className="mt-1 text-sm text-[#667085]">{copy.products.seats(product.seats)} · {copy.products.noRenewalSchedule}</p>
          </div>
          <div className="flex flex-wrap items-center justify-end gap-2">
            <span className="rounded-full bg-[#f2f4f7] px-3 py-1 text-[11px] font-semibold text-[#667085]">{copy.products.noRenewal}</span>
            {actions}
          </div>
        </div>
        <div className="mt-4 h-2 rounded-full border border-dashed border-[#d0d5dd] bg-[#f8fafc]" />
      </div>
    );
  }

  const renewalDays = getDaysUntil(product.renewalDate);
  const renewalClasses = getRenewalClasses(renewalDays);

  return (
    <div className="rounded-[22px] border border-[rgba(0,0,0,0.04)] bg-[#fcfcfd] p-5">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <p className="text-sm font-semibold text-[#101828]">{product.name}</p>
            <p className="mt-1 text-sm text-[#667085]">{copy.products.seats(product.seats)} · {product.renewalDate}</p>
        </div>
        <div className="flex flex-wrap items-center justify-end gap-2">
          <span className={`rounded-full px-3 py-1 text-[11px] font-semibold ${renewalClasses.badge}`}>{copy.products.days(renewalDays)}</span>
          {actions}
        </div>
      </div>
      <div className="mt-4 h-2 overflow-hidden rounded-full bg-[#eaecf0]">
        <div className={`h-full rounded-full ${renewalClasses.bar}`} style={{ width: `${getRenewalProgress(product.renewalDate)}%` }} />
      </div>
    </div>
  );
}

function FormField({ label, children }: { label: string; children: ReactNode }) {
  return (
    <label className="block">
      <span className="text-[10px] font-semibold uppercase tracking-[0.22em] text-[#98a2b3]">{label}</span>
      <div className="mt-2">{children}</div>
    </label>
  );
}

function FormInput(props: InputHTMLAttributes<HTMLInputElement>) {
  return (
    <input
      {...props}
      className={`w-full rounded-2xl border border-[#d0d5dd] bg-white px-4 py-3 text-sm text-[#101828] outline-none transition focus:border-[var(--brand-primary)] focus:ring-2 focus:ring-[var(--brand-primary-ring)] ${props.className ?? ""}`.trim()}
    />
  );
}

function FormTextArea(props: TextareaHTMLAttributes<HTMLTextAreaElement>) {
  return (
    <textarea
      {...props}
      className={`min-h-[120px] w-full rounded-2xl border border-[#d0d5dd] bg-white px-4 py-3 text-sm text-[#101828] outline-none transition focus:border-[var(--brand-primary)] focus:ring-2 focus:ring-[var(--brand-primary-ring)] ${props.className ?? ""}`.trim()}
    />
  );
}

function EmptySectionState({ title, detail }: { title: string; detail: string }) {
  return (
    <div className="rounded-[22px] border border-dashed border-[#d0d5dd] bg-[#fcfcfd] px-5 py-8 text-center">
      <p className="text-sm font-semibold text-[#101828]">{title}</p>
      <p className="mt-2 text-sm text-[#667085]">{detail}</p>
    </div>
  );
}

function CustomerHealthMenu({ value, onChange, copy }: { value: Customer["health"]; onChange: (value: Customer["health"]) => void; copy: CustomerDetailCopy }) {
  const [isOpen, setIsOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (!isOpen) {
      return;
    }

    const handlePointerDown = (event: MouseEvent) => {
      if (containerRef.current?.contains(event.target as Node)) {
        return;
      }

      setIsOpen(false);
    };

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        setIsOpen(false);
      }
    };

    document.addEventListener("mousedown", handlePointerDown);
    document.addEventListener("keydown", handleKeyDown);

    return () => {
      document.removeEventListener("mousedown", handlePointerDown);
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [isOpen]);

  return (
    <div ref={containerRef} className="relative" onClick={event => event.stopPropagation()}>
      <button
        type="button"
        aria-haspopup="menu"
        aria-expanded={isOpen}
        aria-label={copy.page.changeCustomerHealth}
        onClick={() => setIsOpen(current => !current)}
        className={`inline-flex items-center gap-2 rounded-full px-3 py-1 text-[11px] font-semibold transition-all hover:brightness-[0.98] focus:outline-none focus:ring-2 focus:ring-[#006949]/15 ${healthClasses[value]}`}
      >
        <span>{copy.healthLabels[value]}</span>
        <span aria-hidden="true" className="text-[9px] text-current/70">▾</span>
      </button>

      {isOpen ? (
        <div className="absolute left-0 top-full z-20 mt-2 min-w-[11rem] rounded-[22px] border border-[#eaecf0] bg-white p-2 shadow-[0_24px_48px_rgba(15,23,42,0.14)]">
          {customerHealthOptions.map(option => {
            const active = option === value;

            return (
              <button
                key={option}
                type="button"
                role="menuitemradio"
                aria-checked={active}
                onClick={() => {
                  onChange(option);
                  setIsOpen(false);
                }}
                className={`mt-1 flex w-full items-center justify-between rounded-2xl px-4 py-3 text-left text-sm font-semibold transition-colors first:mt-0 hover:bg-[#f8fafc] ${active ? "bg-[#f3f4f6]" : ""}`}
              >
                <span className={`rounded-full px-3 py-1 text-[11px] font-semibold ${healthClasses[option]}`}>{copy.healthLabels[option]}</span>
                {active ? <span className="text-[#006949]">✓</span> : null}
              </button>
            );
          })}
        </div>
      ) : null}
    </div>
  );
}

function ModalShell({
  open,
  dialogTitleId,
  eyebrow,
  title,
  description,
  closeLabel,
  onClose,
  children,
}: {
  open: boolean;
  dialogTitleId: string;
  eyebrow: string;
  title: string;
  description: string;
  closeLabel: string;
  onClose: () => void;
  children: ReactNode;
}) {
  return (
    <AnimatedModal
      open={open}
      onClose={onClose}
      backdropAriaLabel={closeLabel}
      dialogLabelledBy={dialogTitleId}
      overlayClassName="fixed inset-0 z-[90] flex items-center justify-center bg-[#101828]/36 p-6 backdrop-blur-[4px]"
      dialogClassName="app-modal-dialog relative z-10 flex max-h-[calc(100vh-4rem)] w-full max-w-4xl flex-col rounded-[32px] border border-white/70 bg-[#fcfcfd] p-7 shadow-[0_32px_100px_rgba(15,23,42,0.24)]"
    >
        <div className="flex flex-wrap items-start justify-between gap-4">
          <div>
            <p className="text-[10px] font-semibold uppercase tracking-[0.24em] text-[#98a2b3]">{eyebrow}</p>
            <h2 id={dialogTitleId} className="mt-2 font-headline text-3xl font-extrabold tracking-[-0.05em] text-[#101828]">
              {title}
            </h2>
            <p className="mt-2 max-w-2xl text-sm text-[#667085]">{description}</p>
          </div>

          <button
            type="button"
            onClick={onClose}
            className="flex h-11 w-11 items-center justify-center rounded-2xl bg-white text-lg font-semibold text-slate-500 shadow-[0_12px_24px_rgba(15,23,42,0.08)] transition-colors hover:bg-[#f3f4f6] hover:text-[#1a1c1d]"
          >
            ×
          </button>
        </div>

        <div className="mt-6 overflow-y-auto pr-1 pb-2">
          {children}
        </div>
    </AnimatedModal>
  );
}

export function CustomerDetailView({ customer, productPlans, tickets, expenses = [], expenseVendors = [], currency = "USD", teamMembers, timezone, language, currentUser, canEditCustomers = true, canDeleteCustomers = canEditCustomers, onBack, onOpenTicket, onUpdateCustomer, onDeleteCustomer }: CustomerDetailViewProps) {
  const copy = customerDetailViewCopy[language];
  const [isCustomerFormOpen, setIsCustomerFormOpen] = useState(false);
  const [customerDraft, setCustomerDraft] = useState<CustomerProfileDraft>(() => getCustomerProfileDraft(customer));
  const [isContactFormOpen, setIsContactFormOpen] = useState(false);
  const [editingContactId, setEditingContactId] = useState<string | null>(null);
  const [contactDraft, setContactDraft] = useState<ContactDraft>(emptyContactDraft);
  const [isProductFormOpen, setIsProductFormOpen] = useState(false);
  const [editingProductId, setEditingProductId] = useState<string | null>(null);
  const [productDraft, setProductDraft] = useState<ProductDraft>(() => getEmptyProductDraft(productPlans));
  const [isNoteFormOpen, setIsNoteFormOpen] = useState(false);
  const [editingNoteId, setEditingNoteId] = useState<string | null>(null);
  const [noteDraft, setNoteDraft] = useState<NoteDraft>(emptyNoteDraft);
  const hasAvailableItems = productPlans.length > 0;

  const openTickets = tickets.filter(ticket => ticket.status !== "Resolved").length;
  const resolvedTickets = tickets.filter(ticket => ticket.status === "Resolved").length;
  const highPriorityOpen = tickets.filter(ticket => ticket.priority === "High" && ticket.status !== "Resolved").length;
  const sortedNotes = useMemo(() => [...customer.notes].sort((left, right) => new Date(right.createdAt).getTime() - new Date(left.createdAt).getTime()), [customer.notes]);
  const sortedActionHistory = useMemo(
    () => [...customer.actionHistory].sort((left, right) => new Date(right.createdAt).getTime() - new Date(left.createdAt).getTime()),
    [customer.actionHistory],
  );
  const scheduledRenewals = customer.products.filter(product => product.renewalDate).length;
  const openEndedProducts = customer.products.length - scheduledRenewals;
  const totalProductSeats = useMemo(() => getCustomerSeatCount(customer), [customer]);

  const expenseVendorNameById = useMemo(() => new Map(expenseVendors.map(vendor => [vendor.id, vendor.name])), [expenseVendors]);
  const customerExpenses = useMemo(
    () => expenses
      .filter(expense => expense.customerId === customer.id)
      .sort((left, right) => right.expenseDate.localeCompare(left.expenseDate) || right.createdAt.localeCompare(left.createdAt)),
    [expenses, customer.id],
  );
  const customerExpenseTotal = useMemo(() => customerExpenses.reduce((sum, expense) => sum + expense.amount, 0), [customerExpenses]);
  const formatExpenseAmount = useMemo(() => {
    const normalizedCurrency = (currency || "USD").trim().toUpperCase();
    return (amount: number) => {
      try {
        return new Intl.NumberFormat(getIntlLocale(language), { style: "currency", currency: normalizedCurrency, maximumFractionDigits: 2 }).format(amount);
      } catch {
        return `${normalizedCurrency} ${amount.toFixed(2)}`;
      }
    };
  }, [currency, language]);
  const actionHistoryCounts = useMemo(
    () =>
      sortedActionHistory.reduce(
        (totals, entry) => {
          totals[entry.entity] += 1;
          return totals;
        },
        {
          Product: 0,
          Contact: 0,
          "Account status": 0,
        } as Record<CustomerActionHistoryEntry["entity"], number>,
      ),
    [sortedActionHistory],
  );

  useEffect(() => {
    setIsCustomerFormOpen(false);
    setCustomerDraft(getCustomerProfileDraft(customer));
    setIsContactFormOpen(false);
    setEditingContactId(null);
    setContactDraft(emptyContactDraft);
    setIsProductFormOpen(false);
    setEditingProductId(null);
    setProductDraft(getEmptyProductDraft(productPlans));
    setIsNoteFormOpen(false);
    setEditingNoteId(null);
    setNoteDraft(emptyNoteDraft);
  }, [customer.id, productPlans]);

  const applyCustomerUpdate = (updater: (currentCustomer: Customer) => Customer) => {
    onUpdateCustomer(syncCustomerFields(updater(customer)));
  };

  const closeCustomerModal = () => {
    setIsCustomerFormOpen(false);
    setCustomerDraft(getCustomerProfileDraft(customer));
  };

  const handleSaveCustomer = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();

    const company = customerDraft.company.trim();
    const region = customerDraft.region.trim();

    const hasChanges =
      customer.company !== company ||
      customer.region !== region;

    if (!hasChanges) {
      closeCustomerModal();
      return;
    }

    onUpdateCustomer(
      syncCustomerFields({
        ...customer,
        company,
        region,
      }),
    );

    setIsCustomerFormOpen(false);
  };

  const closeContactModal = () => {
    setIsContactFormOpen(false);
    setEditingContactId(null);
    setContactDraft(emptyContactDraft);
  };

  const closeProductModal = () => {
    setIsProductFormOpen(false);
    setEditingProductId(null);
    setProductDraft(getEmptyProductDraft(productPlans));
  };

  const closeNoteModal = () => {
    setIsNoteFormOpen(false);
    setEditingNoteId(null);
    setNoteDraft(emptyNoteDraft);
  };

  const handleHealthChange = (nextHealth: Customer["health"]) => {
    if (nextHealth === customer.health) {
      return;
    }

    onUpdateCustomer(
      appendCustomerActionHistory(
        {
          ...customer,
          health: nextHealth,
        },
        {
          entity: "Account status",
          action: copy.history.actions.updatedCustomerStatus,
          detail: copy.history.details.customerStatusChanged(copy.healthLabels[customer.health], copy.healthLabels[nextHealth]),
          actor: currentUser,
        },
      ),
    );
  };

  const beginContactCreate = () => {
    setEditingContactId(null);
    setContactDraft(emptyContactDraft);
    setIsContactFormOpen(true);
  };

  const beginContactEdit = (contact: CustomerContact) => {
    setEditingContactId(contact.id);
    setContactDraft({ role: contact.role, name: contact.name, email: contact.email, phone: contact.phone ?? "" });
    setIsContactFormOpen(true);
  };

  const handleSaveContact = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();

    const nextContact: CustomerContact = {
      id: editingContactId ?? createRecordId(`${customer.id}-contact`),
      role: contactDraft.role.trim() || "Contact",
      name: contactDraft.name.trim(),
      email: contactDraft.email.trim(),
      phone: contactDraft.phone.trim() || undefined,
    };

    applyCustomerUpdate(currentCustomer =>
      appendCustomerActionHistory(
        {
          ...currentCustomer,
          contacts: sortContacts(
            editingContactId
              ? currentCustomer.contacts.map(contact => (contact.id === editingContactId ? nextContact : contact))
              : [...currentCustomer.contacts, nextContact],
          ),
        },
        {
          entity: "Contact",
          action: editingContactId ? copy.history.actions.updatedContact : copy.history.actions.addedContact,
          detail: copy.history.details.contactSaved(nextContact.name, nextContact.role, Boolean(editingContactId)),
          actor: currentUser,
        },
      ),
    );

    setIsContactFormOpen(false);
    setEditingContactId(null);
    setContactDraft(emptyContactDraft);
  };

  const handleDeleteContact = (contactId: string) => {
    const contact = customer.contacts.find(entry => entry.id === contactId);

    applyCustomerUpdate(currentCustomer =>
      appendCustomerActionHistory(
        {
          ...currentCustomer,
          contacts: currentCustomer.contacts.filter(entry => entry.id !== contactId),
        },
        {
          entity: "Contact",
          action: copy.history.actions.deletedContact,
          detail: copy.history.details.contactRemoved(contact?.name ?? copy.history.entityLabels.Contact),
          actor: currentUser,
        },
      ),
    );

    if (editingContactId === contactId) {
      setIsContactFormOpen(false);
      setEditingContactId(null);
      setContactDraft(emptyContactDraft);
    }
  };

  const beginProductCreate = () => {
    if (!hasAvailableItems) {
      return;
    }

    setEditingProductId(null);
    setProductDraft(getEmptyProductDraft(productPlans));
    setIsProductFormOpen(true);
  };

  const beginProductEdit = (product: CustomerProduct) => {
    const matchedPlan = product.productPlanId
      ? productPlans.find(plan => plan.id === product.productPlanId)
      : productPlans.find(plan => plan.name === product.name);

    setEditingProductId(product.id);
    setProductDraft({ productPlanId: matchedPlan?.id ?? "", seats: product.seats, renewalDate: formatDateForInput(product.renewalDate) });
    setIsProductFormOpen(true);
  };

  const handleSaveProduct = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();

    const selectedProductPlan = productPlans.find(plan => plan.id === productDraft.productPlanId);

    if (!selectedProductPlan) {
      return;
    }

    const nextProduct: CustomerProduct = {
      id: editingProductId ?? createRecordId(`${customer.id}-product`),
      productPlanId: selectedProductPlan.id,
      name: selectedProductPlan.name,
      seats: Math.max(0, productDraft.seats),
      renewalDate: productDraft.renewalDate ? formatDateLabel(productDraft.renewalDate) : undefined,
    };

    applyCustomerUpdate(currentCustomer =>
      appendCustomerActionHistory(
        {
          ...currentCustomer,
          products: editingProductId
            ? currentCustomer.products.map(product => (product.id === editingProductId ? nextProduct : product))
            : [...currentCustomer.products, nextProduct],
        },
        {
          entity: "Product",
          action: editingProductId ? copy.history.actions.updatedProduct : copy.history.actions.addedProduct,
          detail: editingProductId
            ? copy.history.details.productSaved(nextProduct.name, nextProduct.seats, nextProduct.renewalDate)
            : copy.history.details.productAdded(nextProduct.name, nextProduct.seats, nextProduct.renewalDate),
          actor: currentUser,
        },
      ),
    );

    setIsProductFormOpen(false);
    setEditingProductId(null);
    setProductDraft(getEmptyProductDraft(productPlans));
  };

  const handleDeleteProduct = (productId: string) => {
    const product = customer.products.find(entry => entry.id === productId);

    applyCustomerUpdate(currentCustomer =>
      appendCustomerActionHistory(
        {
          ...currentCustomer,
          products: currentCustomer.products.filter(entry => entry.id !== productId),
        },
        {
          entity: "Product",
          action: copy.history.actions.deletedProduct,
          detail: copy.history.details.productRemoved(product?.name ?? copy.history.entityLabels.Product),
          actor: currentUser,
        },
      ),
    );

    if (editingProductId === productId) {
      setIsProductFormOpen(false);
      setEditingProductId(null);
      setProductDraft(getEmptyProductDraft(productPlans));
    }
  };

  const beginNoteCreate = () => {
    setEditingNoteId(null);
    setNoteDraft(emptyNoteDraft);
    setIsNoteFormOpen(true);
  };

  const beginNoteEdit = (note: CustomerNote) => {
    setEditingNoteId(note.id);
    setNoteDraft({ body: note.body });
    setIsNoteFormOpen(true);
  };

  const handleSaveNote = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();

    const existingNote = customer.notes.find(note => note.id === editingNoteId);
    const nextNote: CustomerNote = {
      id: editingNoteId ?? createRecordId(`${customer.id}-note`),
      body: noteDraft.body.trim(),
      createdAt: existingNote?.createdAt ?? new Date().toISOString(),
    };

    applyCustomerUpdate(currentCustomer => ({
      ...currentCustomer,
      notes: editingNoteId
        ? currentCustomer.notes.map(note => (note.id === editingNoteId ? nextNote : note))
        : [nextNote, ...currentCustomer.notes],
    }));

    setIsNoteFormOpen(false);
    setEditingNoteId(null);
    setNoteDraft(emptyNoteDraft);
  };

  const handleDeleteNote = (noteId: string) => {
    applyCustomerUpdate(currentCustomer => ({
      ...currentCustomer,
      notes: currentCustomer.notes.filter(note => note.id !== noteId),
    }));

    if (editingNoteId === noteId) {
      setIsNoteFormOpen(false);
      setEditingNoteId(null);
      setNoteDraft(emptyNoteDraft);
    }
  };

  useEffect(() => {
    if ((!isCustomerFormOpen && !isContactFormOpen && !isProductFormOpen && !isNoteFormOpen) || typeof document === "undefined") {
      return;
    }

    lockBodyScroll();

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key !== "Escape") {
        return;
      }

      if (isCustomerFormOpen) {
        closeCustomerModal();
        return;
      }

      if (isNoteFormOpen) {
        closeNoteModal();
        return;
      }

      if (isProductFormOpen) {
        closeProductModal();
        return;
      }

      closeContactModal();
    };

    document.addEventListener("keydown", handleKeyDown);

    return () => {
      unlockBodyScroll();
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [isCustomerFormOpen, isContactFormOpen, isProductFormOpen, isNoteFormOpen, productPlans]);

  return (
    <section className="space-y-8 p-8">
      <div className="flex flex-wrap items-start justify-between gap-6">
        <div>
          <button
            type="button"
            onClick={onBack}
            className="rounded-full bg-white px-4 py-2 text-[11px] font-semibold uppercase tracking-[0.22em] text-[#475467] shadow-[0_10px_24px_rgba(15,23,42,0.06)] transition-colors hover:bg-[#f3f4f6]"
          >
            ← {copy.page.backToAccounts}
          </button>
          <p className="mt-6 text-[11px] font-semibold uppercase tracking-[0.32em] text-[#98a2b3]">{copy.page.eyebrow}</p>
          <div className="relative mt-3 inline-flex max-w-full items-end pr-12">
            <h1 className="font-headline text-5xl font-extrabold tracking-[-0.06em] text-[#101828]">{customer.company}</h1>
            {canEditCustomers ? (
              <button
                type="button"
                aria-label={copy.page.editCustomerProfile}
                onClick={() => setIsCustomerFormOpen(true)}
                className="absolute right-0 bottom-1 flex h-8 w-8 items-center justify-center rounded-full border border-[#d0d5dd] bg-white text-[#475467] shadow-[0_10px_24px_rgba(15,23,42,0.08)] transition-colors hover:bg-[#f8fafc] hover:text-[#101828]"
              >
                <svg viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="1.6" className="h-4 w-4" aria-hidden="true">
                  <path d="M3.75 13.75V16.25H6.25L14.5833 7.91667L12.0833 5.41667L3.75 13.75Z" strokeLinecap="round" strokeLinejoin="round" />
                  <path d="M10.8333 6.66667L13.3333 9.16667" strokeLinecap="round" strokeLinejoin="round" />
                  <path d="M11.6667 5.83333L12.5 5C13.1904 4.30964 14.3096 4.30964 15 5L15 5C15.6904 5.69036 15.6904 6.80964 15 7.5L14.1667 8.33333" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
              </button>
            ) : null}
          </div>
          <p className="mt-3 max-w-3xl text-sm text-[#667085]">{customer.region || copy.page.locationNotSet}</p>
        </div>

        <div className="flex items-center gap-3">
          {canDeleteCustomers ? (
            <DeleteAccountButton
              onDeleteAccount={() => onDeleteCustomer(customer.id)}
              className="self-center"
            />
          ) : null}

          <div className="rounded-[28px] bg-white px-5 py-4 shadow-[0_18px_50px_rgba(15,23,42,0.06)]">
            <p className="text-[10px] font-semibold uppercase tracking-[0.24em] text-[#98a2b3]">{copy.page.accountHealth}</p>
            <div className="mt-3 flex flex-wrap items-center gap-3">
              {canEditCustomers ? <CustomerHealthMenu value={customer.health} onChange={handleHealthChange} copy={copy} /> : <span className={`rounded-full px-3 py-1 text-[11px] font-semibold ${healthClasses[customer.health]}`}>{copy.healthLabels[customer.health]}</span>}
            </div>
          </div>
        </div>
      </div>

      <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-4">
        <div className="rounded-[24px] bg-white p-6 shadow-[0_20px_60px_rgba(15,23,42,0.06)]">
          <p className="text-[10px] font-semibold uppercase tracking-[0.28em] text-[#98a2b3]">{copy.stats.productSeats}</p>
          <p className="mt-4 text-3xl font-extrabold tracking-[-0.04em] text-[#101828]">{totalProductSeats}</p>
          <p className="mt-2 text-sm text-[#667085]">{copy.stats.productSeatsDetail}</p>
        </div>
        <div className="rounded-[24px] bg-white p-6 shadow-[0_20px_60px_rgba(15,23,42,0.06)]">
          <p className="text-[10px] font-semibold uppercase tracking-[0.28em] text-[#98a2b3]">{copy.stats.openTickets}</p>
          <p className="mt-4 text-3xl font-extrabold tracking-[-0.04em] text-[#101828]">{formatCount(openTickets)}</p>
          <p className="mt-2 text-sm text-[#667085]">{copy.stats.openTicketsDetail}</p>
        </div>
        <div className="rounded-[24px] bg-white p-6 shadow-[0_20px_60px_rgba(15,23,42,0.06)]">
          <p className="text-[10px] font-semibold uppercase tracking-[0.28em] text-[#98a2b3]">{copy.stats.resolved}</p>
          <p className="mt-4 text-3xl font-extrabold tracking-[-0.04em] text-[#101828]">{formatCount(resolvedTickets)}</p>
          <p className="mt-2 text-sm text-[#667085]">{copy.stats.resolvedDetail}</p>
        </div>
        <div className="rounded-[24px] bg-white p-6 shadow-[0_20px_60px_rgba(15,23,42,0.06)]">
          <p className="text-[10px] font-semibold uppercase tracking-[0.28em] text-[#98a2b3]">{copy.stats.priorityRisk}</p>
          <p className="mt-4 text-3xl font-extrabold tracking-[-0.04em] text-[#101828]">{formatCount(highPriorityOpen)}</p>
          <p className="mt-2 text-sm text-[#667085]">{copy.stats.priorityRiskDetail}</p>
        </div>
      </div>

      <div className="grid gap-6 lg:grid-cols-[minmax(0,0.95fr)_minmax(0,1.05fr)]">
        <div className="space-y-6">
          <div className="rounded-[28px] bg-white p-7 shadow-[0_18px_50px_rgba(15,23,42,0.06)]">
            <div className="flex flex-wrap items-center justify-between gap-4">
              <div>
                <h2 className="font-headline text-2xl font-extrabold tracking-[-0.04em] text-[#101828]">{copy.contacts.title}</h2>
                <p className="mt-1 text-sm text-[#667085]">{canEditCustomers ? copy.contacts.editableDescription : copy.contacts.readonlyDescription}</p>
              </div>
              <div className="flex flex-wrap items-center gap-2">
                <span className="rounded-full bg-[#f3f4f6] px-3 py-1 text-[11px] font-semibold text-[#475467]">{copy.contacts.total(customer.contacts.length)}</span>
                {canEditCustomers ? <button type="button" onClick={beginContactCreate} className={mainActionButtonClasses}>{copy.contacts.addContact}</button> : null}
              </div>
            </div>

            <div className="mt-6 space-y-4">
              {customer.contacts.length === 0 ? <EmptySectionState title={copy.contacts.emptyTitle} detail={copy.contacts.emptyDetail} /> : null}
              {customer.contacts.map(contact => (
                <div key={contact.id} className="rounded-[22px] border border-[rgba(0,0,0,0.04)] bg-[#fcfcfd] p-5">
                  <div className="flex flex-wrap items-start justify-between gap-4">
                    <div>
                      <span className="rounded-full bg-white px-3 py-1 text-[11px] font-semibold text-[#475467] shadow-[0_8px_20px_rgba(15,23,42,0.05)]">{contact.role}</span>
                      <p className="mt-3 text-sm font-semibold text-[#101828]">{contact.name}</p>
                      <p className="mt-1 text-sm text-[#101828]">{contact.email}</p>
                      <p className="mt-1 text-sm text-[#667085]">{contact.phone || copy.contacts.noPhone}</p>
                    </div>
                    {canEditCustomers ? (
                      <div className="flex flex-wrap gap-2">
                        <button type="button" onClick={() => beginContactEdit(contact)} className={subtleActionButtonClasses}>
                          {copy.buttons.edit}
                        </button>
                        <button type="button" onClick={() => handleDeleteContact(contact.id)} className={subtleActionButtonClasses}>
                          {copy.buttons.delete}
                        </button>
                      </div>
                    ) : null}
                  </div>
                </div>
              ))}
            </div>

          </div>

          <div className="rounded-[28px] bg-white p-7 shadow-[0_18px_50px_rgba(15,23,42,0.06)]">
            <div className="flex flex-wrap items-start justify-between gap-4">
              <div>
                <h2 className="font-headline text-2xl font-extrabold tracking-[-0.04em] text-[#101828]">{copy.products.title}</h2>
                <p className="mt-1 text-sm text-[#667085]">{copy.products.description}</p>
              </div>
              <div className="flex flex-wrap items-center gap-2">
                <span className="rounded-full bg-[#ecfdf3] px-3 py-1 text-[11px] font-semibold text-[#027a48]">{copy.products.scheduled(scheduledRenewals)}</span>
                <span className="rounded-full bg-[#f2f4f7] px-3 py-1 text-[11px] font-semibold text-[#667085]">{copy.products.openEnded(openEndedProducts)}</span>
                {canEditCustomers ? (
                  <button type="button" onClick={beginProductCreate} disabled={!hasAvailableItems} className={`${mainActionButtonClasses} disabled:cursor-not-allowed disabled:opacity-50`}>
                    {copy.products.addProduct}
                  </button>
                ) : null}
              </div>
            </div>
            <div className="mt-5 space-y-4">
              {!hasAvailableItems ? <EmptySectionState title={copy.products.noItemsTitle} detail={copy.products.noItemsDetail} /> : null}
              {customer.products.length === 0 ? <EmptySectionState title={copy.products.emptyTitle} detail={copy.products.emptyDetail} /> : null}
              {customer.products.map(product => (
                <ProductRenewalRow
                  key={product.id}
                  product={product}
                  copy={copy}
                  actions={canEditCustomers ? (
                    <>
                      <button type="button" onClick={() => beginProductEdit(product)} className={subtleActionButtonClasses}>
                        {copy.buttons.edit}
                      </button>
                      <button type="button" onClick={() => handleDeleteProduct(product.id)} className={subtleActionButtonClasses}>
                        {copy.buttons.delete}
                      </button>
                    </>
                  ) : undefined}
                />
              ))}
            </div>

          </div>

          <div className="rounded-[28px] bg-white p-7 shadow-[0_18px_50px_rgba(15,23,42,0.06)]">
            <div className="flex flex-wrap items-center justify-between gap-4">
              <div>
                <h2 className="font-headline text-2xl font-extrabold tracking-[-0.04em] text-[#101828]">{copy.notes.title}</h2>
                <p className="mt-1 text-sm text-[#667085]">{copy.notes.description}</p>
              </div>
              <div className="flex flex-wrap items-center gap-2">
                <span className="rounded-full bg-[#f3f4f6] px-3 py-1 text-[11px] font-semibold text-[#475467]">{copy.notes.total(sortedNotes.length)}</span>
                {canEditCustomers ? <button type="button" onClick={beginNoteCreate} className={mainActionButtonClasses}>{copy.notes.addNote}</button> : null}
              </div>
            </div>

            <div className="mt-5 space-y-4">
              {sortedNotes.length === 0 ? <EmptySectionState title={copy.notes.emptyTitle} detail={copy.notes.emptyDetail} /> : null}
              {sortedNotes.map(note => (
                <div key={note.id} className="rounded-[22px] border border-[rgba(0,0,0,0.04)] bg-[#fcfcfd] p-5">
                  <div className="flex flex-wrap items-start justify-between gap-4">
                    <div>
                      <p className="text-[10px] font-semibold uppercase tracking-[0.22em] text-[#98a2b3]">{formatNoteTimestamp(note.createdAt, language)}</p>
                      <p className="mt-3 text-sm leading-7 text-[#667085]">{note.body}</p>
                    </div>
                    {canEditCustomers ? (
                      <div className="flex flex-wrap gap-2">
                        <button type="button" onClick={() => beginNoteEdit(note)} className={subtleActionButtonClasses}>
                          {copy.buttons.edit}
                        </button>
                        <button type="button" onClick={() => handleDeleteNote(note.id)} className={subtleActionButtonClasses}>
                          {copy.buttons.delete}
                        </button>
                      </div>
                    ) : null}
                  </div>
                </div>
              ))}
            </div>

          </div>
        </div>

        <div>
          <div className="rounded-[28px] bg-white p-7 shadow-[0_18px_50px_rgba(15,23,42,0.06)]">
            <div className="flex items-center justify-between gap-4">
              <div>
                <h2 className="font-headline text-2xl font-extrabold tracking-[-0.04em] text-[#101828]">{copy.relatedTickets.title}</h2>
                <p className="mt-1 text-sm text-[#667085]">{copy.relatedTickets.description}</p>
              </div>
              <span className="rounded-full bg-[#f3f4f6] px-3 py-1 text-[11px] font-semibold text-[#475467]">{copy.relatedTickets.total(tickets.length)}</span>
            </div>

            <div className="mt-5 space-y-4">
              {tickets.length === 0 ? (
                <div className="rounded-[22px] bg-[#f9fafb] px-5 py-6 text-sm text-[#667085]">{copy.relatedTickets.empty}</div>
              ) : (
                tickets.map(ticket => (
                  <button
                    key={ticket.id}
                    type="button"
                    onClick={() => onOpenTicket(ticket.id)}
                    className="w-full rounded-[22px] border border-[rgba(0,0,0,0.04)] bg-[#fcfcfd] p-5 text-left transition-all hover:border-[rgba(0,105,73,0.14)] hover:bg-white hover:shadow-[0_18px_40px_rgba(15,23,42,0.08)] focus:outline-none focus:ring-2 focus:ring-[#006949]/10"
                  >
                    <div className="flex flex-wrap items-center gap-2">
                      <span className="rounded-full bg-[#f2f4f7] px-3 py-1 text-[11px] font-semibold uppercase tracking-[0.18em] text-[#475467]">{ticket.id}</span>
                      <span className={`rounded-full px-3 py-1 text-[11px] font-semibold ${priorityClasses[ticket.priority]}`}>{copy.priorityLabels[ticket.priority]}</span>
                      <span className={`rounded-full bg-[#f3f4f6] px-3 py-1 text-[11px] font-semibold ${statusClasses[ticket.status]}`}>{copy.statusLabels[ticket.status]}</span>
                    </div>
                    <h3 className="mt-4 text-base font-semibold text-[#101828]">{ticket.subject}</h3>
                    <p className="mt-2 whitespace-pre-line text-sm text-[#667085]">{ticket.description}</p>
                    <div className="mt-4 flex items-center justify-between gap-3 text-sm text-[#667085]">
                      {(() => {
                        const assigneeMember = resolveWorkspaceMember(teamMembers, ticket.assignee);

                        return (
                          <div className="flex items-center gap-3">
                            <UserAvatarBadge
                              avatarUrl={assigneeMember?.avatarUrl ?? null}
                              initials={assigneeMember?.initials ?? getInitials(ticket.assignee ?? copy.relatedTickets.needsOwner)}
                              className="brand-primary-avatar flex h-8 w-8 items-center justify-center rounded-full text-[10px] font-bold"
                            />
                            <span>{ticket.assignee ?? copy.relatedTickets.needsOwner}</span>
                          </div>
                        );
                      })()}
                      <TicketLastUpdateText value={ticket.lastUpdate} timezone={timezone} className="text-sm text-[#667085]" as="span" />
                    </div>
                  </button>
                ))
              )}
            </div>
          </div>

          <div className="mt-6 rounded-[28px] bg-white p-7 shadow-[0_18px_50px_rgba(15,23,42,0.06)]">
            <div className="flex items-center justify-between gap-4">
              <div>
                <h2 className="font-headline text-2xl font-extrabold tracking-[-0.04em] text-[#101828]">{copy.relatedExpenses.title}</h2>
                <p className="mt-1 text-sm text-[#667085]">{copy.relatedExpenses.description}</p>
              </div>
              <div className="flex flex-col items-end gap-1">
                <span className="rounded-full bg-[#f3f4f6] px-3 py-1 text-[11px] font-semibold text-[#475467]">{copy.relatedExpenses.total(customerExpenses.length)}</span>
                {customerExpenses.length > 0 ? (
                  <span className="text-sm font-bold text-[#101828]">{formatExpenseAmount(customerExpenseTotal)}</span>
                ) : null}
              </div>
            </div>

            <div className="mt-5 space-y-4">
              {customerExpenses.length === 0 ? (
                <div className="rounded-[22px] bg-[#f9fafb] px-5 py-6 text-sm text-[#667085]">{copy.relatedExpenses.empty}</div>
              ) : (
                customerExpenses.map(expense => (
                  <div
                    key={expense.id}
                    className="rounded-[22px] border border-[rgba(0,0,0,0.04)] bg-[#fcfcfd] p-5"
                  >
                    <div className="flex flex-wrap items-start justify-between gap-3">
                      <div className="min-w-0">
                        <p className="text-base font-semibold text-[#101828]">{expenseVendorNameById.get(expense.vendorId) ?? copy.relatedExpenses.unknownVendor}</p>
                        <p className="mt-1 text-sm text-[#667085]">{expense.description || copy.relatedExpenses.noDescription}</p>
                        <p className="mt-2 text-[10px] font-semibold uppercase tracking-[0.22em] text-[#98a2b3]">{formatDateLabel(expense.expenseDate)}</p>
                      </div>
                      <span className="text-base font-bold text-[#101828]">{formatExpenseAmount(expense.amount)}</span>
                    </div>
                    {(expense.invoices?.length ?? 0) > 0 ? (
                      <ul className="mt-3 flex flex-wrap gap-2">
                        {expense.invoices!.map(invoice => (
                          <li key={invoice.key}>
                            <a
                              href={invoice.downloadUrl}
                              target="_blank"
                              rel="noreferrer"
                              className="inline-flex max-w-[220px] items-center gap-1 truncate rounded-full bg-white px-3 py-1 text-xs font-semibold text-[var(--brand-primary)] shadow-[0_8px_20px_rgba(15,23,42,0.05)] transition-colors hover:underline"
                            >
                              {invoice.fileName}
                            </a>
                          </li>
                        ))}
                      </ul>
                    ) : null}
                  </div>
                ))
              )}
            </div>
          </div>

          <div className="mt-6 rounded-[28px] bg-white p-7 shadow-[0_18px_50px_rgba(15,23,42,0.06)]">
            <div className="flex flex-wrap items-start justify-between gap-4">
              <div>
                <h2 className="font-headline text-2xl font-extrabold tracking-[-0.04em] text-[#101828]">{copy.history.title}</h2>
                <p className="mt-1 text-sm text-[#667085]">{copy.history.description}</p>
              </div>
              <span className="rounded-full bg-[#f3f4f6] px-3 py-1 text-[11px] font-semibold text-[#475467]">{copy.history.events(sortedActionHistory.length)}</span>
            </div>

            <div className="mt-5 flex flex-wrap gap-2">
              <span className="rounded-full bg-[#ecfdf3] px-3 py-1 text-[11px] font-semibold text-[#027a48]">{copy.history.products(actionHistoryCounts.Product)}</span>
              <span className="rounded-full bg-[#eff8ff] px-3 py-1 text-[11px] font-semibold text-[#175cd3]">{copy.history.contacts(actionHistoryCounts.Contact)}</span>
              <span className="rounded-full bg-[#f2f4f7] px-3 py-1 text-[11px] font-semibold text-[#475467]">{copy.history.status(actionHistoryCounts["Account status"])}</span>
            </div>

            <div className="mt-5 space-y-4">
              {sortedActionHistory.length === 0 ? (
                <EmptySectionState title={copy.history.emptyTitle} detail={copy.history.emptyDetail} />
              ) : (
                sortedActionHistory.map(entry => (
                  <div key={entry.id} className="rounded-[22px] border border-[rgba(0,0,0,0.04)] bg-[#fcfcfd] p-5">
                    <div className="flex flex-wrap items-start justify-between gap-4">
                      <div>
                        <div className="flex flex-wrap items-center gap-2">
                          <span className={`rounded-full px-3 py-1 text-[11px] font-semibold ${historyEntityClasses[entry.entity]}`}>{copy.history.entityLabels[entry.entity]}</span>
                          <span className="text-[10px] font-semibold uppercase tracking-[0.22em] text-[#98a2b3]">{formatNoteTimestamp(entry.createdAt, language)}</span>
                        </div>
                        <p className="mt-3 text-sm font-semibold text-[#101828]">{entry.action}</p>
                        <p className="mt-2 text-sm text-[#667085]">{entry.detail}</p>
                      </div>
                      <span className="rounded-full bg-white px-3 py-1 text-[11px] font-semibold text-[#475467] shadow-[0_8px_20px_rgba(15,23,42,0.05)]">{entry.actor}</span>
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>
        </div>
      </div>

      <ModalShell
          open={isCustomerFormOpen && canEditCustomers}
          dialogTitleId="customer-profile-dialog-title"
          eyebrow={copy.forms.customer.eyebrow}
          title={customer.company}
          description={copy.forms.customer.description}
          closeLabel={copy.buttons.closeDialog}
          onClose={closeCustomerModal}
        >
          <form onSubmit={handleSaveCustomer}>
            <div className="grid gap-4 md:grid-cols-2">
              <FormField label={copy.forms.customer.company}>
                <FormInput value={customerDraft.company} onChange={event => setCustomerDraft(current => ({ ...current, company: event.target.value }))} placeholder={copy.forms.customer.companyPlaceholder} required />
              </FormField>
              <FormField label={copy.forms.customer.location}>
                <FormInput value={customerDraft.region} onChange={event => setCustomerDraft(current => ({ ...current, region: event.target.value }))} placeholder={copy.forms.customer.locationPlaceholder} required />
              </FormField>
            </div>
            <div className="mt-4 flex flex-wrap justify-end gap-2">
              <button type="button" onClick={closeCustomerModal} className={modalCancelButtonClasses}>
                {copy.buttons.cancel}
              </button>
              <button type="submit" className={modalPrimaryButtonClasses}>
                {copy.forms.customer.save}
              </button>
            </div>
          </form>
        </ModalShell>

      <ModalShell
          open={isContactFormOpen && canEditCustomers}
          dialogTitleId="customer-contact-dialog-title"
          eyebrow={editingContactId ? copy.forms.contact.editEyebrow : copy.forms.contact.createEyebrow}
          title={editingContactId ? contactDraft.name || copy.forms.contact.defaultTitle : copy.forms.contact.newTitle}
          description={copy.forms.contact.description}
          closeLabel={copy.buttons.closeDialog}
          onClose={closeContactModal}
        >
          <form onSubmit={handleSaveContact}>
            <div className="grid gap-4 md:grid-cols-2">
              <FormField label={copy.forms.contact.role}>
                <FormInput value={contactDraft.role} onChange={event => setContactDraft(current => ({ ...current, role: event.target.value }))} placeholder={copy.forms.contact.rolePlaceholder} required />
              </FormField>
              <FormField label={copy.forms.contact.name}>
                <FormInput value={contactDraft.name} onChange={event => setContactDraft(current => ({ ...current, name: event.target.value }))} placeholder={copy.forms.contact.namePlaceholder} required />
              </FormField>
              <FormField label={copy.forms.contact.email}>
                <FormInput type="email" value={contactDraft.email} onChange={event => setContactDraft(current => ({ ...current, email: event.target.value }))} placeholder={copy.forms.contact.emailPlaceholder} required />
              </FormField>
              <FormField label={copy.forms.contact.phone}>
                <FormInput value={contactDraft.phone} onChange={event => setContactDraft(current => ({ ...current, phone: event.target.value }))} placeholder={copy.forms.contact.phonePlaceholder} />
              </FormField>
            </div>
            <div className="mt-4 flex flex-wrap justify-end gap-2">
              <button type="button" onClick={closeContactModal} className={modalCancelButtonClasses}>
                {copy.buttons.cancel}
              </button>
              <button type="submit" className={modalPrimaryButtonClasses}>
                {editingContactId ? copy.forms.contact.save : copy.forms.contact.create}
              </button>
            </div>
          </form>
        </ModalShell>

      <ModalShell
          open={isProductFormOpen && canEditCustomers}
          dialogTitleId="customer-product-dialog-title"
          eyebrow={editingProductId ? copy.forms.product.editEyebrow : copy.forms.product.createEyebrow}
          title={editingProductId ? copy.forms.product.editTitle : copy.forms.product.createTitle}
          description={copy.forms.product.description}
          closeLabel={copy.buttons.closeDialog}
          onClose={closeProductModal}
        >
          <form onSubmit={handleSaveProduct}>
            <div className="grid gap-4 md:grid-cols-2">
              <FormField label={copy.forms.product.item}>
                <select
                  value={productDraft.productPlanId}
                  onChange={event => setProductDraft(current => ({ ...current, productPlanId: event.target.value }))}
                  className="w-full rounded-2xl border border-[#d0d5dd] bg-white px-4 py-3 text-sm text-[#101828] outline-none transition focus:border-[var(--brand-primary)] focus:ring-2 focus:ring-[var(--brand-primary-ring)]"
                  required
                >
                  <option value="" disabled>
                    {copy.products.selectItem}
                  </option>
                  {productPlans.map(plan => (
                    <option key={plan.id} value={plan.id}>
                      {plan.name} · {plan.category}
                    </option>
                  ))}
                </select>
              </FormField>
              <FormField label={copy.forms.product.seats}>
                <FormInput type="number" min={0} value={productDraft.seats} onChange={event => setProductDraft(current => ({ ...current, seats: Number(event.target.value) || 0 }))} required />
              </FormField>
              <FormField label={copy.forms.product.renewalDate}>
                <FormInput type="date" value={productDraft.renewalDate} onChange={event => setProductDraft(current => ({ ...current, renewalDate: event.target.value }))} />
              </FormField>
            </div>
            {editingProductId && !productDraft.productPlanId ? (
              <p className="mt-4 text-sm text-[#b42318]">{copy.products.inactiveItemError}</p>
            ) : null}
            <div className="mt-4 flex flex-wrap justify-end gap-2">
              <button type="button" onClick={closeProductModal} className={modalCancelButtonClasses}>
                {copy.buttons.cancel}
              </button>
              <button type="submit" className={modalPrimaryButtonClasses}>
                {editingProductId ? copy.forms.product.save : copy.forms.product.create}
              </button>
            </div>
          </form>
        </ModalShell>

      <ModalShell
          open={isNoteFormOpen && canEditCustomers}
          dialogTitleId="customer-note-dialog-title"
          eyebrow={editingNoteId ? copy.forms.note.editEyebrow : copy.forms.note.createEyebrow}
          title={editingNoteId ? copy.forms.note.editTitle : copy.forms.note.createTitle}
          description={copy.forms.note.description}
          closeLabel={copy.buttons.closeDialog}
          onClose={closeNoteModal}
        >
          <form onSubmit={handleSaveNote}>
            <FormField label={copy.forms.note.note}>
              <FormTextArea value={noteDraft.body} onChange={event => setNoteDraft({ body: event.target.value })} placeholder={copy.notes.addAccountContext} required />
            </FormField>
            <div className="mt-4 flex flex-wrap justify-end gap-2">
              <button type="button" onClick={closeNoteModal} className={modalCancelButtonClasses}>
                {copy.buttons.cancel}
              </button>
              <button type="submit" className={modalPrimaryButtonClasses}>
                {editingNoteId ? copy.forms.note.save : copy.forms.note.create}
              </button>
            </div>
          </form>
        </ModalShell>
    </section>
  );
}